import React, { useState, useEffect, useRef } from "react";
import {
  isValidAuPhone,
  isValidNzPhone,
  sanitizeInput,
  showToast
} from "../../../../utils/utils";
import { useParams } from "react-router-dom";
import { showToastType } from "../../../../utils/constants/appConstants";
import {
  customerTab,
  passwordConfirmModal,
  tooltips,
  validatePhoneNumberTxt,
  responseStatus,
  Inputfieldvalidation
} from "../../../../locales/en";
import {
  getOnboardAccountantUrl,
  getOnboardUrl,
  getPasswordConfirmationURL
} from "../../../../utils/constants/apiEndPoints";

import privateAxios from "../../../../utils/axiosPrivate";
import { HTTP_STATUS_SUCCESS } from "../../../../utils/constants/appConstants";
import PasswordSetModal from "../../../../components/modals/PasswordSetModal";
import styles from "./../../../../assets/scss/custom.module.scss";
import { Tooltip as ReactTooltip } from "react-tooltip";

const Marketing = () => {
  const { id } = useParams();
  const hasFetchedRef = useRef(false);

  const tooltipsArray = [
    {
      id: "tooltip-validateContact",
      content: tooltips.validateContact,
      place: "top"
    }
  ];

  const validatePassword = async (password) => {
    try {
      const response = await privateAxios.post(getPasswordConfirmationURL, {
        password,
        method: "PUT",
        endpoint: getOnboardUrl
      });
      return response.status === 200;
    } catch (error) {
      showToast(
        error?.response?.data?.message || passwordConfirmModal.validationFailed,
        showToastType.error
      );
      return false;
    }
  };
  const validatePhoneNumber = (phone, { allowEmpty = true } = {}) => {
    if (!phone || !phone.trim()) {
      if (allowEmpty) {
        return { isValid: true, errorMsg: "" };
      }
      return {
        isValid: false,
        errorMsg: Inputfieldvalidation?.errorMsg?.enterContactNumber
      };
    }

    const trimmedPhone = phone.trim();

    const invalidCharPattern = /[^0-9+]/;
    if (invalidCharPattern.test(trimmedPhone)) {
      return {
        isValid: false,
        errorMsg: Inputfieldvalidation?.errorMsg?.enterContactNumber
      };
    }

    if (!trimmedPhone.startsWith("+61") && !trimmedPhone.startsWith("+64")) {
      return {
        isValid: false,
        errorMsg: validatePhoneNumberTxt?.errorMsg?.startNzAu
      };
    }

    if (trimmedPhone.startsWith("+64")) {
      if (!isValidNzPhone(trimmedPhone)) {
        return {
          isValid: false,
          errorMsg: validatePhoneNumberTxt.errorMsg.nzPhRequired
        };
      }
    }

    if (trimmedPhone.startsWith("+61")) {
      if (!isValidAuPhone(trimmedPhone)) {
        return {
          isValid: false,
          errorMsg: validatePhoneNumberTxt.errorMsg.auPhRequired
        };
      }
    }

    return { isValid: true, errorMsg: "" };
  };

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingValidationToggle, setPendingValidationToggle] = useState(null);

  const initialAccountantData = {
    userAccountId: "",
    hearAboutUs: "",
    accountingFirmName: "",
    accountantName: "",
    contactNumber: "",
    isAccountantContactNumberValidated: false
  };
  const [accountantData, setAccountantData] = useState(initialAccountantData);
  const [errors, setErrors] = useState({});
  const [isDisable, setIsDisable] = useState(true);

  useEffect(() => {
    if (!id) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    const fetchAccountantData = async () => {
      try {
        const response = await privateAxios.get(getOnboardAccountantUrl(id));
        if (
          response.data.status === HTTP_STATUS_SUCCESS &&
          response.data.data
        ) {
          setAccountantData(response.data.data);
        } else {
          showToast(response.data.message, showToastType.error);
        }
      } catch (error) {
        showToast(
          error.response?.data?.errors?.[0] || responseStatus.errorText,
          showToastType.error
        );
      }
    };
    fetchAccountantData();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAccountantData((prev) => ({ ...prev, [name]: value }));
    if (name !== "contactNumber" && errors.contactNumber) {
      setErrors((prev) => ({
        ...prev,
        contactNumber: ""
      }));
    }
  };

  const saveNameFieldData = async (name, value) => {
    try {
      const updateDto = {
        userAccountId: accountantData.userAccountId || id,
        hearAboutUs: accountantData.hearAboutUs,
        accountingFirmName: sanitizeInput(accountantData.accountingFirmName),
        accountantName: sanitizeInput(accountantData.accountantName),
        contactNumber: accountantData.contactNumber,
        isAccountantContactNumberValidated:
          accountantData.isAccountantContactNumberValidated
      };

      if (
        name === "accountingFirmName" ||
        name === "accountantName" ||
        name === "contactNumber"
      ) {
        updateDto[name] = value;
      }

      if (name === "isAccountantContactNumberValidated") {
        updateDto.isAccountantContactNumberValidated =
          !accountantData.isAccountantContactNumberValidated;
      }

      const response = await privateAxios.put(getOnboardUrl, updateDto, {
        headers: { "Content-Type": "application/json" }
      });

      if (response.data.status === HTTP_STATUS_SUCCESS) {
        showToast(response.data.message, showToastType.success);
        setIsDisable(true);
        setAccountantData(response.data.data);
      } else {
        showToast(response.data.message, showToastType.error);
      }
    } catch (error) {
      showToast(error?.response?.data?.message, showToastType.error);
    }
  };

  const handleContactInputBlur = async (e) => {
    const { name, value } = e.target;

    if (!name) return;

    if (name === "contactNumber") {
      const { isValid, errorMsg } = validatePhoneNumber(value, {
        allowEmpty: true
      });

      if (!isValid) {
        setErrors((prev) => ({
          ...prev,
          contactNumber: errorMsg
        }));
        return;
      }

      setErrors((prev) => ({
        ...prev,
        contactNumber: ""
      }));
    }

    await saveNameFieldData(name, value);
  };

  const handleClickContactNumber = () => {
    setIsDisable(false);
  };

  const handleContactNumberChange = (e) => {
    handleInputChange(e);

    if (errors.contactNumber) {
      setErrors((prev) => ({
        ...prev,
        contactNumber: ""
      }));
    }
  };

  const handleValidateContactClick = () => {
    if (accountantData?.isAccountantContactNumberValidated) {
      setPendingValidationToggle(false);
      setShowPasswordModal(true);
      return;
    }

    const phone = accountantData?.contactNumber || "";
    const { isValid, errorMsg } = validatePhoneNumber(phone, {
      allowEmpty: false
    });

    if (!isValid) {
      setErrors((prev) => ({
        ...prev,
        contactNumber: errorMsg
      }));
      return;
    }

    setErrors((prev) => ({
      ...prev,
      contactNumber: ""
    }));

    setPendingValidationToggle(true);
    setShowPasswordModal(true);
  };

  return (
    <>
      <PasswordSetModal
        isOpen={showPasswordModal}
        toggle={() => {
          setShowPasswordModal(false);
          setPendingValidationToggle(null);
        }}
        handlePasswordEntered={async (password) => {
          const isValid = await validatePassword(password);
          setShowPasswordModal(false);
          if (isValid && pendingValidationToggle !== null) {
            saveNameFieldData(
              "isAccountantContactNumberValidated",
              !accountantData.isAccountantContactNumberValidated
            );
            setPendingValidationToggle(null);
          }
        }}
      />
      <div>
        <div className={styles.titleArea}>
          <h5>{customerTab?.marketing.title}</h5>
        </div>

        <div className="row">
          <div className="col-12 col-xxl-4 mb-3">
            <div className="blog-input-wrap">
              <div className={styles.labelTitle}>
                <h5>{customerTab?.marketing.accName}</h5>
              </div>
              <div className="inputtextfield">
                <input
                  type="text"
                  className={styles.textfield}
                  name="accountantName"
                  value={accountantData?.accountantName ?? ""}
                  onChange={handleInputChange}
                  onBlur={handleContactInputBlur}
                />
              </div>
            </div>
          </div>
          <div className="col-12 col-xxl-4 mb-3">
            <div className="blog-input-wrap">
              <div className={styles.labelTitle}>
                <h5>{customerTab?.marketing.firm}</h5>
              </div>
              <div className="inputtextfield">
                <input
                  type="text"
                  name="accountingFirmName"
                  value={accountantData?.accountingFirmName ?? ""}
                  className={styles.textfield}
                  onChange={handleInputChange}
                  onBlur={handleContactInputBlur}
                />
              </div>
            </div>
          </div>
          <div className="col-12 col-xxl-4 mb-3">
            <div className="blog-input-wrap">
              <div className={styles.labelTitle}>
                <h5>{customerTab?.marketing.contactNo}</h5>
              </div>
              <div
                className={`${styles.inputWithButton} d-flex align-items-center`}
              >
                <div className="inputtextfield hideApperance">
                  <input
                    type="text"
                    name="contactNumber"
                    className={styles.textfield}
                    onClick={handleClickContactNumber}
                    onChange={handleContactNumberChange}
                    onBlur={handleContactInputBlur}
                    value={accountantData?.contactNumber ?? ""}
                    readOnly={isDisable && !errors.contactNumber}
                    maxLength="15"
                  />
                </div>
                <span
                  className={`${styles.btnPrimary} ${
                    accountantData?.isAccountantContactNumberValidated
                      ? "validatebtn"
                      : "unvalidatebtn"
                  } right-icon added-icon`}
                >
                  <div className="inputtextfield hideApperance">
                    <input
                      type="button"
                      onClick={handleValidateContactClick}
                      data-tooltip-id="tooltip-validateContact"
                      data-tooltip-content={tooltips.validateContact}
                      className={
                        accountantData?.isAccountantContactNumberValidated
                          ? ""
                          : styles.invalidateBtnGray
                      }
                    />
                  </div>
                </span>
              </div>
              {errors.contactNumber && (
                <div className="text-danger small">{errors.contactNumber}</div>
              )}
            </div>
          </div>
          <div className="col-12 col-xxl-4 mb-3">
            <div className="blog-input-wrap">
              <div className={`${styles.labelTitle} border-bottom`}>
                <h5>{customerTab?.marketing.heardFrom}</h5>
              </div>
              <p>{accountantData?.hearAboutUs}</p>
            </div>
          </div>
          <div className="col-12 col-xxl-4 mb-3">
            {/* <div className="blog-input-wrap">
              <div className={`${styles.labelTitle} border-bottom`}>
                <h5>{customerTab?.marketing.GCLID}</h5>
              </div>
              <p>{accountantData?.gclid || ""}</p>
            </div> */}
          </div>
        </div>
      </div>

      {tooltipsArray.map(({ id, content, place }) => (
        <ReactTooltip
          key={id}
          className="tooltipcustom"
          id={id}
          place={place}
          content={content}
        />
      ))}
    </>
  );
};

export default Marketing;
